import { HorizontalAlignment, OpenUrlWithKind } from '@api/generated/models';
import { Spacer, Text } from '@cellula/react';
import { tokens } from '@cellula/react-theme-patient';
import { Icon, IconType } from '@components/icon.component';
import { VStack } from '@components/v_stack.component';
import { getAbsoluteRoute } from '@helpers/route';
import type { Disposer } from '@modules/state/state';
import { AuthService } from '@services/auth/auth.service';
import { FeatureFlagService } from '@services/feature_flag/feature_flag.service';
import type { FlexibleListedAtReceptionsModel } from '@services/flexible_component/mod';
import { FlexibleButtonIconModel } from '@services/flexible_component/mod';
import { theme } from '@services/theme.service/theme';
import { UnitService } from '@services/unit.service';
import { Scoped, useService } from '@visiba/instantiation';
import { Presentation, usePresentation } from '@visiba/presentation';
import { rem } from '@visiba/std';
import { Fragment, useEffect } from 'react';
import styled from 'styled-components';
import { Routeable } from './_flexible_identifier_routeable.component';

interface Props {
	component: FlexibleListedAtReceptionsModel;
}

const elements = {
	container: styled.div`
		margin: ${rem(8)} 0;
		display: flex;
		flex-direction: column;
	`,
	button: styled.div`
		display: flex;
		align-items: center;
		width: 100%;
		background: ${theme.primary};
		border-radius: ${rem(10)};
		min-height: ${rem(64)};
		padding-top: ${tokens.spacing.x2};
		padding-bottom: ${tokens.spacing.x2};
		color: #fff;
	`,
	icon: styled.div`
		margin: 0 ${rem(20)};
		width: ${rem(24)};
		height: ${rem(24)};
		display: flex;
		justify-content: center;
		align-items: center;
		font-size: ${rem(24)};
		color: #fff;
		flex-shrink: 0;
	`,
};

interface FlexibleListedAtReceptionsButtonIconProps {
	component: FlexibleButtonIconModel;
	__TEMP__overrideNavigation?: (url: string) => void;
}

function FlexibleListedAtReceptionsButtonIcon({
	component,
	__TEMP__overrideNavigation,
	...rest
}: FlexibleListedAtReceptionsButtonIconProps): JSX.Element {
	return (
		<Routeable
			url={component.url}
			requiresAuthentication={component.requiresAuthentication}
			openWith={component.openUrlWith}
			{...rest}
		>
			<elements.button data-testid='FlexibleButtonIcon'>
				<elements.icon>
					<Icon type={component.icon?.origin as IconType} />
				</elements.icon>

				<VStack fluid={false}>
					<Text variant='bodyLargeRegular'>{component.title}</Text>
				</VStack>
			</elements.button>
		</Routeable>
	);
}

interface ListedAtReceptionsPresentation {
	items: FlexibleButtonIconModel[];
	shouldRender: boolean;
}

@Scoped()
class Controller {
	public readonly presentation = Presentation.create<ListedAtReceptionsPresentation>({
		items: [],
		shouldRender: false,
	});
	private authStateDisposer: Disposer | null = null;

	constructor(
		private readonly unitService: UnitService,
		private readonly authService: AuthService,
		private readonly featureFlagService: FeatureFlagService,
	) {
		this.load();

		this.authStateDisposer = this.authService.events.on('onSuccessfulAuthentication', () => {
			this.load();
		});
	}

	public teardown(): void {
		if (this.authStateDisposer) {
			this.authStateDisposer();
			this.authStateDisposer = null;
		}
	}

	private load(): void {
		let hideListedAtReceptions = false;

		try {
			hideListedAtReceptions = this.featureFlagService.checkFlag('PatientWeb.HideListedAtReceptions');
		} catch (error) {
			console.error('Failed to resolve feature flag "PatientWeb.HideListedAtReceptions". Falling back to default value.', error);
			hideListedAtReceptions = false;
		}

		if (!this.authService.isAuthenticated() || hideListedAtReceptions) {
			this.presentation.write({
				items: [],
				shouldRender: false,
			});

			return;
		}

		let items: FlexibleButtonIconModel[] = [];

		try {
			items = Array.from(this.unitService.units.values())
				.filter((unit) => unit.iAmListedHere && unit.receptionId > 0)
				.sort((a, b) => a.name.localeCompare(b.name))
				.map(
					(unit) =>
						new FlexibleButtonIconModel({
							id: unit.receptionId,
							order: -1,
							title: unit.name,
							subtitle: '',
							url: getAbsoluteRoute(unit.route),
							openUrlWith: OpenUrlWithKind.SameTab,
							horizontalAlignment: HorizontalAlignment.End,
							icon: {
								origin: 'home2',
								type: 'local',
							},
							hasBrandedNavbar: false,
							openAnotherApp: false,
							requiresAuthentication: false,
							channel: null,
						}),
				);
		} catch {
			items = [];
		}

		this.presentation.write({
			items,
			shouldRender: items.length > 0,
		});
	}
}

export function FlexibleListedAtReceptions({ component }: Props): JSX.Element {
	const controller = useService(Controller);
	const presentation = usePresentation(controller.presentation);

	useEffect(() => {
		return () => {
			controller.teardown();
		};
	}, [controller]);

	return (
		<Fragment>
			{presentation.shouldRender && presentation.items.length > 0 ? (
				<elements.container data-testid='FlexibleListedAtReceptions'>
					<Text variant='bodySmallRegular'>{component.title}</Text>
					<Spacer vertical={rem(4)} />

					<VStack spacing={8}>
						{presentation.items.map((item) => (
							<FlexibleListedAtReceptionsButtonIcon key={item.id} component={item} />
						))}
					</VStack>
				</elements.container>
			) : null}
		</Fragment>
	);
}
